import type { IntegrationsRow, WorkspaceIntegrationsRow } from 'app/types/integrations'
import { isSMCOrg } from 'lib/isSMCOrg'

export type IntegrationName = IntegrationsRow[`integration`]

/**
 * Integrations whose location-level form depends on a workspace-level
 * connection existing first (credentials or OAuth token).
 * Gravity Forms is intentionally absent: its location form generates its own
 * webhook key and works without the workspace-level integration.
 */
export const WORKSPACE_DEPENDENT_INTEGRATIONS: readonly IntegrationName[] = [
  `CallRail`,
  `Carestack`,
  `Ascend`,
  `RingCentral`,
  `Twilio`,
  `Facebook Ads`,
  `Facebook Leads`,
]

export function requiresWorkspaceSetup(integration: IntegrationName | undefined, orgId: string | undefined): boolean {
  if (!integration) return false
  // The workspace picker hides Facebook Ads for the SMC org; mirror that gate.
  if (integration === `Facebook Ads` && isSMCOrg(orgId)) return false
  return WORKSPACE_DEPENDENT_INTEGRATIONS.includes(integration)
}

export function configuredWorkspaceIntegrations(rows: WorkspaceIntegrationsRow[]): Set<IntegrationName> {
  return new Set(rows.map((r) => r.integration))
}

const RESUME_KEY = `integrationWizardResume`

/** Marks a wizard-initiated OAuth redirect so the return trip can resume at Step 2. */
export function setWizardResume(integration: IntegrationName): void {
  sessionStorage.setItem(RESUME_KEY, integration)
}

/** Reads and clears the resume flag. Always clears, so stale flags cannot fire later. */
export function consumeWizardResume(): IntegrationName | undefined {
  const value = sessionStorage.getItem(RESUME_KEY)
  if (value !== null) sessionStorage.removeItem(RESUME_KEY)
  return (value as IntegrationName | null) ?? undefined
}
